import { AlertCircle, CheckCircle, Clock, MapPin, Package, Truck, XCircle } from 'lucide-react';
import React from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';

interface TrackingEvent {
  eventDate: Date;
  eventTime: string;
  eventDescription: string;
  eventLocation: string;
  eventType: 'MANUAL' | 'API';
  eventState: string;
  createdDate: Date;
}

interface TrackingTimelineProps {
  events: TrackingEvent[];
}

export const TrackingTimeline: React.FC<TrackingTimelineProps> = ({ events }) => {
  const getEventIcon = (eventState: string, eventDescription: string) => {
    const state = eventState?.toLowerCase() ?? '';
    const description = eventDescription?.toLowerCase() ?? '';
    
    if (state.includes('delivered') || description.includes('delivered')) {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    }
    if (state.includes('cancelled') || description.includes('cancelled')) {
      return <XCircle className="h-5 w-5 text-red-600" />;
    }
    if (state.includes('returned') || description.includes('returned')) {
      return <AlertCircle className="h-5 w-5 text-orange-600" />;
    }
    if (state.includes('transit') || description.includes('transit')) {
      return <Truck className="h-5 w-5 text-blue-600" />;
    }
    if (state.includes('picked') || description.includes('picked')) {
      return <Package className="h-5 w-5 text-purple-600" />;
    }
    
    return <MapPin className="h-5 w-5 text-gray-600" />;
  };

  const getEventColor = (eventState: string) => {
    const state = eventState?.toLowerCase() ?? '';
    
    if (state.includes('delivered')) return 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800';
    if (state.includes('cancelled')) return 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
    if (state.includes('returned')) return 'bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800';
    if (state.includes('transit')) return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    if (state.includes('picked')) return 'bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800';
    
    return 'bg-slate-50 border-slate-200 dark:bg-slate-900/30 dark:border-slate-800';
  };

  const formatDateTime = (date: Date | string, time?: string) => {
    const eventDate = new Date(date);
    if (Number.isNaN(eventDate.getTime())) {
      return 'Unknown time';
    }
    const dateStr = eventDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    
    if (time) {
      return `${dateStr} at ${time}`;
    }
    
    return `${dateStr} at ${eventDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  const getEventTypeColor = (eventType: 'MANUAL' | 'API') => {
    return eventType === 'API' ? 'bg-blue-600 dark:bg-blue-500' : 'bg-slate-600 dark:bg-slate-500';
  };

  const getEventTimestamp = (event: TrackingEvent) => {
    const eventDate = new Date(event.eventDate);
    if (!Number.isNaN(eventDate.getTime())) {
      if (event.eventTime) {
        const [hours, minutes] = event.eventTime.split(':').map(Number);
        if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
          const withTime = new Date(eventDate);
          withTime.setHours(hours, minutes, 0, 0);
          return withTime.getTime();
        }
      }
      return eventDate.getTime();
    }

    const createdDate = new Date(event.createdDate);
    if (!Number.isNaN(createdDate.getTime())) {
      return createdDate.getTime();
    }

    return 0;
  };

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500 dark:text-slate-400">
        <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No tracking events available</p>
      </div>
    );
  }

  // Sort events by date (newest first) and filter out internal AWB Updated events
  const sortedEvents = [...events]
    .filter(event => !event.eventDescription?.toLowerCase().includes('awb updated'))
    .sort((a, b) => 
      getEventTimestamp(b) - getEventTimestamp(a)
    );

  return (
    <div className="space-y-4">
      {sortedEvents.map((event, index) => (
        <Card key={index} className={`${getEventColor(event.eventState)} border-l-4 relative`}>
          <CardContent className="p-4">
            <div className="flex items-start space-x-4">
              {/* Event Icon */}
              <div className="flex-shrink-0 mt-1">
                {getEventIcon(event.eventState, event.eventDescription)}
              </div>
              
              {/* Event Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1 break-words">
                      {event.eventDescription}
                    </h4>
                    
                    <div className="flex items-center space-x-4 text-xs text-slate-600 dark:text-slate-300 mb-2">
                      <span className="flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {event.eventLocation || 'Location unavailable'}
                      </span>
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDateTime(event.eventDate, event.eventTime)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Badge 
                        variant="secondary" 
                        className={`${getEventTypeColor(event.eventType)} text-white text-xs`}
                      >
                        {event.eventType}
                      </Badge>
                      <Badge variant="outline" className="text-xs text-slate-700 dark:text-slate-200">
                        {event.eventState.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Timeline Connector */}
                  {index < sortedEvents.length - 1 && (
                    <div className="absolute left-6 mt-8 w-px h-8 bg-slate-300 dark:bg-slate-700" />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* Timeline End */}
      <div className="flex items-center space-x-4 pl-4">
        <div className="flex-shrink-0">
          <div className="h-5 w-5 rounded-full bg-slate-300 dark:bg-slate-700 flex items-center justify-center">
            <div className="h-2 w-2 rounded-full bg-white dark:bg-slate-200" />
          </div>
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Shipment created
        </div>
      </div>
    </div>
  );
};
